import { useEffect, useRef } from 'react';
import { animate, motion, useInView, useMotionValue, useTransform } from 'framer-motion';

// CountUp — animates a number counting from `from` to `to` when scrolled
// into view. For pure-number displays; for prefixed/suffixed values use the
// `prefix` and `suffix` props, or pass a custom `format` function for richer
// formatting (e.g., currency with comma separators).
//
// Props:
//   to        - final value
//   from      - starting value (default 0)
//   duration  - in seconds (default 1.5)
//   decimals  - decimal places (default 0)
//   prefix    - text before number (e.g., '$')
//   suffix    - text after number (e.g., '%')
//   delay     - seconds to wait AFTER the viewport trigger before starting
//               the count-up (default 0). Use this when a count-up needs to
//               land in sync with a choreographed entrance sequence — e.g.,
//               PricingComparisonSection waits ~1.2s after the section enters
//               view so the prices count up as their cards finish sliding in.
//   amount    - viewport intersection threshold 0..1 (default 0.5). Lower
//               this when you need the count-up to trigger as soon as the
//               element is barely visible — useful for choreographed sections
//               where the timing is controlled by `delay` rather than by the
//               element's own intersection.
//   format    - optional (value: number) => string. Overrides the default
//               prefix + decimals + suffix formatting. Pass when you need
//               locale-aware comma separators or any custom formatting.
//   className - className passthrough
//   style     - inline style passthrough (useful when the count-up sits
//               inside a gradient-text-clip parent and needs to inherit
//               specific text styling at the motion.span level).
//
// Animation fires once, never replays. Uses the same ease-out curve as
// FadeIn for consistency.

export default function CountUp({
  to,
  from = 0,
  duration = 1.5,
  decimals = 0,
  prefix = '',
  suffix = '',
  delay = 0,
  amount = 0.5,
  format,
  className = '',
  style,
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount });
  // AEO/SSR: the motion value starts at the FINAL value, not `from`. The
  // build-time prerender (scripts/prerender.mjs) captures the first render
  // frame — starting at `from` shipped "0 Verified reviews", "0.0 Google
  // rating" and "$0" prices to every non-JS AI crawler. Starting at `to`
  // means crawlers (and the pre-hydration paint) see the true number, and
  // the first client render matches the server HTML (hydration-safe). The
  // count-up itself is untouched: on scroll-into-view the value snaps to
  // `from` and animates up exactly as before.
  const motionValue = useMotionValue(to);
  const rounded = useTransform(motionValue, (latest) => {
    if (format) return format(latest);
    const factor = Math.pow(10, decimals);
    return `${prefix}${(Math.round(latest * factor) / factor).toFixed(decimals)}${suffix}`;
  });

  useEffect(() => {
    if (!isInView) return undefined;

    motionValue.set(from);
    let controls;
    const timer = setTimeout(() => {
      controls = animate(motionValue, to, {
        duration,
        ease: [0.16, 1, 0.3, 1],
      });
    }, delay * 1000);

    return () => {
      clearTimeout(timer);
      if (controls) controls.stop();
    };
  }, [isInView, motionValue, from, to, duration, delay]);

  return (
    <motion.span ref={ref} className={className} style={style}>
      {rounded}
    </motion.span>
  );
}
